// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * PDF viewer lifecycle management for the browser client.
 *
 * Owns PdfjsViewer instances per view, unload timers, and page persistence.
 */
import type { InitialState } from "../../shared/src/types";
import { getBookViewId, getEnabledBooks, resolveMultikeyJsonUrl } from "../../shared/src/songbook";
import { serializeViewUrl } from "../../shared/src/view-url";
import { Logger } from "../../shared/src/generic/logger";
import { createEngagementConfirmation, isQualifyingPdfInteraction } from "./statistics-engagement";
import type { Translator } from "@unilarva/translator";
import { openMultikeySession, type MultikeySession } from "./multikey-session";
import type { SongIndexClient } from "./song-index-client";
import { displaySongTitle } from "../../shared/src/song-index";
import {
  createHashNavigationStateAdapter,
  formatPdfDate,
  PDFJS_VIEWER_UI_HOOKS,
  PdfjsViewer,
  type PdfjsViewerDocumentInformation,
  type PdfjsViewerDocumentInformationResult,
  type PdfjsViewerRenderingProfile,
  type PdfjsViewerUiBindings,
  type PdfjsViewerUiTextOptions,
  type PdfjsViewerLogger,
  type PdfjsViewerRuntime,
} from "@unilarva/pdfjs-viewer";

/** Default inactivity timeout for unloading PdfjsViewer instances (in minutes). */
const DEFAULT_PDF_UNLOAD_INACTIVE_MINUTES = 60;
const PDF_RUNTIME_CACHE = "sb-runtime-v2";

export function replacePdfBindingLifetime(
  lifetimes: Map<string, AbortController>,
  view: string,
): AbortController {
  lifetimes.get(view)?.abort();
  const lifetime = new AbortController();
  lifetimes.set(view, lifetime);
  return lifetime;
}

export function releasePdfBindingLifetime(
  lifetimes: Map<string, AbortController>,
  view: string,
): void {
  lifetimes.get(view)?.abort();
  lifetimes.delete(view);
}

/** Routes package diagnostics through the application's configurable logger. */
export const pdfjsViewerLogger: PdfjsViewerLogger = entry => {
  const prefix = `[pdfjs-viewer:${entry.component}:${entry.event}]`;
  const context = {
    ...(entry.viewerId ? { viewerId: entry.viewerId } : {}),
    ...(entry.details ?? {}),
  };
  const values: unknown[] = Object.keys(context).length ? [context] : [];
  if (entry.error !== undefined) values.push(entry.error);

  if (entry.level === "error") Logger.error(prefix, entry.message, ...values);
  else Logger.debug(prefix, entry.message, ...values);
};

/**
 * Manages PdfjsViewer instances per view, including lazy initialization,
 * inactivity-based unloading, and last-page persistence.
 */
export class PdfManager {
  private readonly state: InitialState;
  private readonly translator: Translator;
  private readonly runtime: PdfjsViewerRuntime;
  private readonly pageStorePrefix = "sb_pdf_page:";

  private readonly viewers = new Map<string, PdfjsViewer>();
  private readonly unloadTimers = new Map<string, number>();
  private readonly bindingLifetimes = new Map<string, AbortController>();
  private readonly multikeySetups = new Map<PdfjsViewer, Promise<void>>();
  private readonly multikeyReady = new WeakSet<PdfjsViewer>();
  private readonly multikeyOutputs = new WeakMap<PdfjsViewer, MultikeySession | null>();
  private readonly multikeyStatuses = new Map<PdfjsViewer, HTMLElement>();
  private readonly pendingPdfDestinations = new Map<string, string>();
  private readonly destinationRequests = new Map<string, AbortController>();
  private activeView: string = "home";
  private readonly confirmEngagement: () => void;

  private readonly viewMeta = new Map<
    string,
    { sourceId: string; variant: string; indexUrl: string | null }
  >();

  constructor(
    state: InitialState,
    translator: Translator,
    runtime: PdfjsViewerRuntime,
    private readonly songIndex?: Pick<SongIndexClient, "getSongsBySongId">,
  ) {
    this.state = state;
    this.confirmEngagement = createEngagementConfirmation(state.statisticsVisitToken);
    this.translator = translator;
    this.runtime = runtime;

    const enabled = getEnabledBooks(this.state.books);
    enabled.forEach((book, idx0) => {
      const id = idx0 + 1;
      const viewId = getBookViewId(book, id);
      const sourceId = String((book as any).source_book_id || "").trim();
      const variant = String((book as any).variant || "default").trim();
      this.viewMeta.set(viewId, {
        sourceId,
        variant,
        indexUrl: resolveMultikeyJsonUrl(this.state.books, book),
      });
    });

    // PdfManager and this listener share the application's lifetime.
    this.translator.subscribe(() => {
      const options = this.createViewerUiTextOptions();
      for (const viewer of this.viewers.values()) viewer.setUiText(options);
      for (const element of this.multikeyStatuses.values())
        element.textContent = this.translator.translateKey("pdf.transpose-unavailable");
    });
  }

  /** Creates package-owned dynamic text; static custom markup remains data-i18n-owned. */
  private createViewerUiTextOptions(): PdfjsViewerUiTextOptions {
    return {
      labels: {
        searchPreparing: this.translator.translateKey("search.loading"),
        outlinePreparing: this.translator.translateKey("pdf.outline-loading"),
        outlineUntitled: this.translator.translateKey("pdf.outline-untitled"),
        thumbnailError: this.translator.translateKey("pdf.thumbnail-error"),
        layersPreparing: this.translator.translateKey("pdf.layers-loading"),
        layersPreparationError: this.translator.translateKey("pdf.layers-error"),
        noLayers: this.translator.translateKey("pdf.layers-empty"),
        resetLayers: this.translator.translateKey("pdf.layers-reset"),
        fallbackLayer: this.translator.translateKey("pdf.layer-fallback"),
        fallbackLayerGroup: this.translator.translateKey("pdf.layer-group-fallback"),
        attachmentsPreparing: this.translator.translateKey("pdf.attachments-loading"),
        attachmentsPreparationError: this.translator.translateKey("pdf.attachments-error"),
        noAttachments: this.translator.translateKey("pdf.attachments-empty"),
        attachmentDownload: this.translator.translateKey("button.download"),
        attachmentDownloadError: this.translator.translateKey("pdf.attachment-download-error"),
        annotationComment: this.translator.translateKey("pdf.annotation-comment"),
        annotationAttachment: this.translator.translateKey("pdf.annotation-attachment"),
        annotationAttachmentError: this.translator.translateKey("pdf.annotation-attachment-error"),
        xfaUnavailable: this.translator.translateKey("pdf.xfa-unavailable"),
        xfaHybridFallback: this.translator.translateKey("pdf.xfa-hybrid-fallback"),
        documentProgressError: this.translator.translateKey("pdf.loading-error"),
        printRangeInvalid: this.translator.translateKey("pdf.print-range-invalid"),
        printMemoryLimitExceeded: this.translator.translateKey("pdf.print-memory-limit-exceeded"),
        printPreparationUnavailable: this.translator.translateKey("pdf.print-output-error"),
        printUnsupportedPlatform: this.translator.translateKey("pdf.print-unsupported"),
        printSystemDialogGuidance: this.translator.translateKey("pdf.print-system-settings"),
        printKeepPortraitGuidance: this.translator.translateKey("pdf.print-keep-portrait"),
        printDuplexPortraitFlipGuidance: this.translator.translateKey("pdf.print-duplex-portrait"),
        printDuplexLandscapeFlipGuidance: this.translator.translateKey(
          "pdf.print-duplex-landscape",
        ),
        printPreparationCancelGuidance: this.translator.translateKey(
          "pdf.print-preparation-cancel",
        ),
        printSourceRangeWarning: this.translator.translateKey("pdf.print-source-range"),
        printSourceLayerWarning: this.translator.translateKey("pdf.print-source-layers"),
        printSourceParityWarning: this.translator.translateKey("pdf.print-source-parity"),
        printSourceQualityWarning: this.translator.translateKey("pdf.print-source-quality"),
      },
      formatters: {
        thumbnailPage: page =>
          `${this.translator.translateKey("pdf.thumbnail-page").trim() || "Page"} ${page}`,
        printSummary: summary =>
          this.translator.translateKey("pdf.print-summary", {
            values: {
              pages: summary.pages,
              sheetCount: summary.sheetCount,
              orientation: this.translator
                .translateKey(`pdf.print-${summary.orientation}`)
                .toLocaleLowerCase(),
              layout: this.translator.translateKey(
                summary.layout === "spread"
                  ? "pdf.print-summary-spread"
                  : "pdf.print-summary-single",
              ),
              dpi: summary.dpi,
            },
          }),
        printProgress: (completed, total) =>
          this.translator.translateKey("pdf.print-progress", { values: { completed, total } }),
        printSheetLimitExceeded: (sheetCount, maxSheets) =>
          this.translator.translateKey("pdf.print-sheet-limit-exceeded", {
            values: { sheetCount, maxSheets },
          }),
        printReducedQuality: dpi =>
          this.translator.translateKey("pdf.print-reduced", { values: { dpi } }),
        printReducedExplanation: dpi =>
          this.translator.translateKey("pdf.print-reduced-explanation", { values: { dpi } }),
      },
    };
  }

  /**
   * Called whenever the active view changes so the manager can update PDF state.
   */
  handleViewChange(previousView: string, nextView: string): void {
    const isPdfView = (v: string): boolean => this.viewMeta.has(v);
    const destinationHashParam = String(
      (this.state.site as any).pdfDestinationHashParam || "",
    ).trim();

    this.activeView = nextView;
    this.destinationRequests.get(previousView)?.abort();
    this.destinationRequests.delete(previousView);
    this.destinationRequests.get(nextView)?.abort();
    this.destinationRequests.delete(nextView);

    // Deactivate the old view's viewer immediately so UI overlays (e.g. outline)
    // cannot cover the next view. State is preserved and restored on re-activate.
    if (isPdfView(previousView)) {
      this.viewers.get(previousView)?.setActive(false);
    }

    if (previousView !== nextView && isPdfView(previousView)) {
      this.pendingPdfDestinations.delete(previousView);
      this.schedulePdfUnload(previousView);
    }

    this.cancelPdfUnload(nextView);

    if (!isPdfView(nextView)) {
      // Non-PDF view (home/search): clear any shareable-anchor hash parameter.
      if (destinationHashParam) this.replaceHashParam(destinationHashParam, null);

      return;
    }

    const target = destinationHashParam
      ? new URLSearchParams(window.location.hash.slice(1)).get(destinationHashParam)?.trim()
      : null;
    if (target) this.pendingPdfDestinations.set(nextView, target);
    else this.pendingPdfDestinations.delete(nextView);
    void this.ensurePdfViewer(nextView);
  }

  /** Replays a link requested after loading began, without trusting a hash overwritten by initial layout. */
  private applyPendingPdfDestination(view: string, viewer: PdfjsViewer): void {
    const target = this.pendingPdfDestinations.get(view);
    if (!target || this.activeView !== view || viewer.state.status !== "ready") return;
    this.pendingPdfDestinations.delete(view);
    const prefix = String((this.state.site as any).pdfNamedDestinationPrefix || "").trim();
    if (!prefix) return;
    const request = new AbortController();
    this.destinationRequests.get(view)?.abort();
    this.destinationRequests.set(view, request);
    void viewer
      .navigateToPdfNamedDestination(prefix + target, { signal: request.signal })
      .catch(error => {
        if (!request.signal.aborted) Logger.debug("PDF song destination unavailable", error);
      })
      .finally(() => {
        if (this.destinationRequests.get(view) === request) this.destinationRequests.delete(view);
      });
  }

  /**
   * Returns the configured unload timeout for inactive PdfjsViewer instances in ms.
   */
  private pdfUnloadInactiveMs(): number {
    const minutesRaw = (this.state.site as any).pdfUnloadInactiveMinutes;
    const minutes = Number(minutesRaw ?? DEFAULT_PDF_UNLOAD_INACTIVE_MINUTES);
    if (!Number.isFinite(minutes) || minutes <= 0) return 0;
    return minutes * 60 * 1000;
  }

  /** Cancels any scheduled unload timer for the given view's PdfjsViewer. */
  private cancelPdfUnload(view: string): void {
    const t = this.unloadTimers.get(view);
    if (t != null) {
      window.clearTimeout(t);
      this.unloadTimers.delete(view);
    }
  }

  /**
   * Destroys the PdfjsViewer for a view and clears its heavy DOM content.
   */
  private unloadPdfViewer(view: string): void {
    this.cancelPdfUnload(view);

    const viewer = this.viewers.get(view);
    if (!viewer) {
      this.releaseViewerBindings(view);
      return;
    }

    // Safety: never unload the currently active view.
    if (this.activeView === view) return;

    // A retained native job still owns the shared setup's package listeners.
    if (viewer.state.print.nativeResourcesRetained) {
      this.schedulePdfUnload(view);
      return;
    }

    this.releaseViewerBindings(view);
    try {
      viewer.destroy();
    } catch {
      // ignore
    }
    this.viewers.delete(view);

    const pane = document.querySelector<HTMLElement>(`[data-sb-pane="${this.cssEscape(view)}"]`);
    const pdfRootEl = pane?.querySelector<HTMLElement>(".pdf-viewer");
    const container = pdfRootEl?.querySelector<HTMLElement>(".pdf-container");
    if (container) container.innerHTML = "";

    const outline = pdfRootEl?.querySelector<HTMLElement>(".pdf-outline");
    if (outline) outline.innerHTML = "";
    const thumbnails = pdfRootEl?.querySelector<HTMLElement>(".pdf-thumbnails");
    if (thumbnails) thumbnails.innerHTML = "";
    const attachments = pdfRootEl?.querySelector<HTMLElement>(".pdf-attachments");
    if (attachments) attachments.innerHTML = "";
    const layers = pdfRootEl?.querySelector<HTMLElement>(".pdf-layers");
    if (layers) layers.innerHTML = "";

    const pageCount = pdfRootEl?.querySelector<HTMLElement>(".pdf-page-count");
    if (pageCount) pageCount.textContent = "?";

    const pageNum = pdfRootEl?.querySelector<HTMLInputElement>(".pdf-page-number-input");
    if (pageNum) pageNum.value = "1";
  }

  /**
   * Schedules unloading of a PdfjsViewer after a period of inactivity.
   */
  private schedulePdfUnload(view: string): void {
    if (view === "home" || view === "search") return;
    if (!this.viewers.has(view)) return;

    const delay = this.pdfUnloadInactiveMs();
    if (delay <= 0) return;

    this.cancelPdfUnload(view);
    const t = window.setTimeout(() => this.unloadPdfViewer(view), delay);
    this.unloadTimers.set(view, t);
  }

  /**
   * Returns the last viewed page (1-based) for a given view, if one is stored.
   */
  private getSavedPdfPage(view: string): number | null {
    try {
      const saved = Number(localStorage.getItem(this.pageStorePrefix + view) || "");
      if (!Number.isFinite(saved) || saved < 1) return null;
      return Math.floor(saved);
    } catch {
      return null;
    }
  }

  /** Lazily initializes or reuses a viewer and applies the current route's captured target. */
  private async ensurePdfViewer(view: string): Promise<void> {
    this.cancelPdfUnload(view);

    const existing = this.viewers.get(view);
    if (existing) {
      existing.setActive(true);
      this.updateAdvancedSearchLink(view);
      this.applyPendingPdfDestination(view, existing);
      const indexUrl = this.viewMeta.get(view)?.indexUrl;
      const lifetime = this.bindingLifetimes.get(view);
      if (indexUrl && lifetime && existing.state.status === "ready") {
        const root = document.querySelector<HTMLElement>(
          `[data-sb-pane="${this.cssEscape(view)}"] .pdf-viewer`,
        );
        const url = root?.dataset.sbPdfUrl;
        if (root && url) this.startMultikey(view, existing, root, url, indexUrl, lifetime);
      }
      return;
    }

    const pane = document.querySelector<HTMLElement>(`[data-sb-pane="${this.cssEscape(view)}"]`);
    if (!pane) return;

    const pdfRootEl = pane.querySelector<HTMLElement>(".pdf-viewer");
    if (!pdfRootEl) return;

    const pdfUrl = pdfRootEl.dataset.sbPdfUrl;
    if (!pdfUrl) return;
    const indexUrl = this.viewMeta.get(view)?.indexUrl ?? null;

    if (!navigator.onLine) {
      try {
        const cache = await caches.open(PDF_RUNTIME_CACHE);
        const cached = await cache.match(new URL(pdfUrl, window.location.href).href, {
          ignoreVary: true,
        });
        if (!cached) {
          if (this.activeView === view) this.showPdfUnavailableMessage(pdfRootEl);
          return;
        }
      } catch {
        // Continue through PDF.js if Cache Storage cannot be inspected.
      }

      // The user may have navigated elsewhere while Cache Storage was queried.
      if (this.activeView !== view || this.viewers.has(view)) return;
    }

    const initialPage = this.getSavedPdfPage(view) ?? undefined;
    const uiBindings = this.collectUiBindings(pdfRootEl);
    if (!uiBindings) return;

    // Optional shareable-anchor tracking via PDF named destinations.
    const shareableNamedDestinationPrefix =
      String((this.state.site as any).pdfNamedDestinationPrefix || "").trim() || undefined;
    const navigationDestinationHashParam =
      String((this.state.site as any).pdfDestinationHashParam || "").trim() || undefined;
    const navigationState = navigationDestinationHashParam
      ? createHashNavigationStateAdapter(
          { navigationDestinationParam: navigationDestinationHashParam },
          window,
        )
      : undefined;

    const viewer = new PdfjsViewer({
      rootEl: pdfRootEl,
      ui: {
        mode: "custom",
        ...this.createViewerUiTextOptions(),
      },
      uiBindings,
      runtime: this.runtime,
      fontRendering: "geometric",
      logger: pdfjsViewerLogger,
      // A configured reader denies output until pairing/restoration settles. Failed or empty
      // initialization retains ordinary output; no companion bytes ever become its identity.
      printOutputProvider: indexUrl
        ? context => {
            if (this.activeView !== view) return null;
            const session = this.multikeyOutputs.get(viewer);
            if (session) return session.admitOutput(context.signal);
            if (session === undefined) return null;
            const document = viewer.state.formDirty ? "with-form-values" : "original";
            return {
              resolveSource: async () => {
                const result = await viewer.getDocumentData({ document });
                if (!result.ok) throw new Error(`Original PDF unavailable: ${result.reason}`);
                return result.data;
              },
            };
          }
        : undefined,
      // A complete same-origin response lets the Service Worker cache each book
      // reliably and synthesize PDF.js byte ranges when the network is unavailable.
      defaultDocumentOptions: {
        standardFontDataUrl: "/assets/pdfjs-standard-fonts/",
        ...(new URL(pdfUrl, window.location.href).origin === window.location.origin
          ? { disableRange: true }
          : {}),
      },
      pageLayout: "auto",
      renderingProfile: "auto",
      screenWakeLock: "always",
      viewerId: view,
      keyboard: { scope: "global" },
      shareableNamedDestinationPrefix,
      navigationState: navigationState
        ? {
            readNavigationDestinationId: () =>
              this.activeView === view
                ? (this.pendingPdfDestinations.get(view) ??
                  navigationState.readNavigationDestinationId())
                : null,
            writeNavigationDestinationId: id => {
              if (this.activeView === view) navigationState.writeNavigationDestinationId(id);
            },
          }
        : undefined,
      behavior: { textSelectionPersistence: "until-copy" },
      features: {
        textSelection: true,
        search: true,
        outline: true,
        thumbnails: true,
        attachments: true,
        annotationLinks: { internalDestinations: true, externalUrls: true },
        annotationMarkup: { popups: true, fileAttachments: true },
        forms: { interactive: true, xfa: true },
        layers: true,
        print: {
          mode: "native",
          pageSources: indexUrl ? "display" : "base",
          defaults: {
            sheet: "a4",
            pageScaling: "fit",
            orientation: "auto",
            quality: { dpi: 300 },
          },
        },
      },
    });
    this.syncRenderingProfileControls(
      uiBindings.menu?.renderingProfile ?? null,
      viewer.state.availableRenderingProfiles,
    );
    viewer.setActive(true);
    this.viewers.set(view, viewer);
    this.bindViewerEvents(view, pdfRootEl, viewer, uiBindings);
    const lifetime = this.bindingLifetimes.get(view)!;
    const current = (): boolean => !lifetime.signal.aborted && this.viewers.get(view) === viewer;
    try {
      // Canonical identity, destinations and original downloads stay on this PDF.
      const loaded = await viewer.load(pdfUrl, { initialPage });
      if (!current() || (!loaded.ok && loaded.reason === "cancelled")) return;
      if (loaded.ok && indexUrl)
        this.startMultikey(view, viewer, pdfRootEl, pdfUrl, indexUrl, lifetime);
    } finally {
      if (current()) this.applyPendingPdfDestination(view, viewer);
    }
  }

  /** Metadata/conversion startup never blocks ordinary reading or canonical destination admission. */
  private startMultikey(
    view: string,
    viewer: PdfjsViewer,
    root: HTMLElement,
    canonicalUrl: string,
    indexUrl: string,
    lifetime: AbortController,
  ): void {
    if (
      this.multikeyReady.has(viewer) ||
      this.multikeySetups.has(viewer) ||
      lifetime.signal.aborted
    )
      return;
    root.querySelector(".sb-multikey-init-status")?.remove();
    this.multikeyStatuses.delete(viewer);
    const current = (): boolean => !lifetime.signal.aborted && this.viewers.get(view) === viewer;
    this.multikeyOutputs.delete(viewer);
    const setup = openMultikeySession({
      viewer,
      root,
      canonicalUrl,
      indexUrl,
      bookId: this.viewMeta.get(view)?.sourceId || view,
      translator: this.translator,
      songTitle: async occurrence => {
        const songs = (await this.songIndex?.getSongsBySongId(occurrence.songId))?.filter(
          song => song.songId === occurrence.songId,
        );
        const song =
          songs?.find(
            song => song.viewId === view && song.page === (occurrence.pageIndex ?? -1) + 1,
          ) ??
          songs?.find(song => song.viewId === view) ??
          songs?.[0];
        return song ? displaySongTitle(song) : occurrence.songId;
      },
      signal: lifetime.signal,
      isActive: () => current() && this.activeView === view,
    })
      .then(session => {
        if (!current()) {
          session?.dispose();
          return;
        }
        this.multikeyReady.add(viewer);
        this.multikeyOutputs.set(viewer, session);
      })
      .catch(error => {
        if (!current()) return;
        this.multikeyOutputs.set(viewer, null);
        Logger.warn("Key changes unavailable; keeping the ordinary reader", error);
        const status = root.ownerDocument.createElement("span");
        status.className = "sb-sr-only sb-multikey-init-status";
        status.setAttribute("role", "status");
        status.textContent = this.translator.translateKey("pdf.transpose-unavailable");
        root.append(status);
        this.multikeyStatuses.set(viewer, status);
      })
      .finally(() => {
        if (this.multikeySetups.get(viewer) === setup) this.multikeySetups.delete(viewer);
      });
    this.multikeySetups.set(viewer, setup);
  }

  /** Replaces the complete per-view host binding lifetime during initialization. */
  private bindViewerEvents(
    view: string,
    pdfRootEl: HTMLElement,
    viewer: PdfjsViewer,
    uiBindings?: PdfjsViewerUiBindings,
  ): void {
    const bindingLifetime = replacePdfBindingLifetime(this.bindingLifetimes, view);
    pdfRootEl.addEventListener("pdf:ready", () => this.applyPendingPdfDestination(view, viewer), {
      signal: bindingLifetime.signal,
    });
    bindingLifetime.signal.addEventListener(
      "abort",
      () => {
        this.pendingPdfDestinations.delete(view);
        this.destinationRequests.get(view)?.abort();
        this.destinationRequests.delete(view);
        pdfRootEl.querySelector(".sb-multikey-init-status")?.remove();
        this.multikeyStatuses.delete(viewer);
      },
      { once: true },
    );
    const confirmInput = (event: Event): void => {
      if (
        this.activeView === view &&
        viewer.state.pageCount > 0 &&
        isQualifyingPdfInteraction(event)
      ) {
        this.confirmEngagement();
      }
    };
    for (const type of ["pointerdown", "wheel", "keydown"]) {
      pdfRootEl.addEventListener(type, confirmInput, {
        capture: true,
        passive: true,
        signal: bindingLifetime.signal,
      });
    }
    // The viewer also accepts navigation shortcuts when focus is on the document body.
    document.addEventListener(
      "keydown",
      event => {
        if (
          event.target === document.body &&
          [
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
            "PageUp",
            "PageDown",
            "Home",
            "End",
            " ",
          ].includes(event.key)
        )
          confirmInput(event);
      },
      { signal: bindingLifetime.signal },
    );
    pdfRootEl.addEventListener(
      "pdf:pagechange",
      (ev: Event) => {
        const page = Number((ev as CustomEvent).detail?.page);
        if (!Number.isFinite(page) || page < 1) return;
        try {
          localStorage.setItem(this.pageStorePrefix + view, String(page));
        } catch {
          /* Reading remains available without storage. */
        }
      },
      { signal: bindingLifetime.signal },
    );

    pdfRootEl.addEventListener(
      "pdf:error",
      () => {
        this.showPdfUnavailableMessage(pdfRootEl);
      },
      { signal: bindingLifetime.signal },
    );

    const syncModeLabels = (): void => {
      const state = viewer?.state;
      if (!state) return;
      const apply = (button: HTMLButtonElement | undefined, key: string): void => {
        if (!button) return;
        button.dataset.i18nTitle = key;
        button.dataset.i18nAriaLabel = key;
        const label = this.translator.translateKey(key);
        button.title = label;
        button.setAttribute("aria-label", label);
      };
      apply(
        uiBindings?.fullscreen?.toggle,
        state.fullscreen ? "pdf.exit-fullscreen" : "pdf.fullscreen",
      );
      apply(
        uiBindings?.presentation?.toggle,
        state.presentationMode ? "pdf.exit-presentation" : "pdf.presentation",
      );
    };
    pdfRootEl.addEventListener("pdf:statechange", syncModeLabels, {
      signal: bindingLifetime.signal,
    });
    syncModeLabels();

    this.bindDocumentInformation(
      pdfRootEl,
      viewer,
      bindingLifetime.signal,
      uiBindings?.menu?.panel,
      uiBindings?.menu?.toggle,
      () => viewer.getDocumentInformation(),
    );
    this.wireAdvancedSearchLink(view, pdfRootEl, bindingLifetime.signal, uiBindings?.search?.input);
  }

  /** Owns the parent application's translated document-information presentation. */
  private bindDocumentInformation(
    root: HTMLElement,
    viewer: PdfjsViewer,
    signal: AbortSignal,
    menuPanel?: HTMLElement,
    menuToggle?: HTMLButtonElement,
    informationQuery: () => Promise<PdfjsViewerDocumentInformationResult> = () =>
      viewer.getDocumentInformation(),
  ): void {
    const action = root.querySelector<HTMLButtonElement>(".sb-pdf-document-information-btn");
    const dialog = root.querySelector<HTMLDialogElement>(".sb-pdf-document-information");
    const closeButton = dialog?.querySelector<HTMLButtonElement>(
      ".sb-pdf-document-information-close",
    );
    const acceptButton = dialog?.querySelector<HTMLButtonElement>(
      ".sb-pdf-document-information-accept",
    );
    const status = dialog?.querySelector<HTMLElement>(".sb-pdf-document-information-status");
    if (!action || !dialog || !closeButton || !acceptButton || !status) return;

    const fields = [
      "title",
      "author",
      "subject",
      "keywords",
      "creationDate",
      "modificationDate",
      "creator",
      "producer",
      "pdfFormatVersion",
      "pageCount",
      "language",
      "isLinearized",
    ] as const satisfies readonly (keyof PdfjsViewerDocumentInformation)[];
    const rows = new Map(
      fields.map(field => [
        field,
        dialog.querySelector<HTMLElement>(`[data-sb-pdf-information-field="${field}"]`),
      ]),
    );
    let request = 0;
    let opener: HTMLElement | null = null;

    const setStatus = (key: string | null): void => {
      if (key === null) {
        status.removeAttribute("data-i18n");
        status.textContent = "";
        return;
      }
      status.dataset.i18n = key;
      status.textContent = this.translator.translateKey(key);
    };
    const clearRows = (): void => {
      for (const row of rows.values()) {
        if (!row) continue;
        row.hidden = true;
        const value = row.querySelector<HTMLElement>("dd");
        value?.removeAttribute("data-i18n");
        if (value) value.textContent = "";
      }
    };
    const dismiss = (): void => {
      request++;
      dialog.removeAttribute("aria-busy");
      if (dialog.open) dialog.close();
    };
    const render = (information: Readonly<PdfjsViewerDocumentInformation>): void => {
      for (const field of fields) {
        const row = rows.get(field);
        const target = row?.querySelector<HTMLElement>("dd");
        if (!row || !target) continue;
        const raw = information[field];
        if (raw === null || raw === "") {
          row.hidden = true;
          continue;
        }
        row.hidden = false;
        if (typeof raw === "boolean") {
          const key = raw ? "general.yes" : "general.no";
          target.dataset.i18n = key;
          target.textContent = this.translator.translateKey(key);
        } else {
          target.removeAttribute("data-i18n");
          target.textContent =
            typeof raw === "string" && (field === "creationDate" || field === "modificationDate")
              ? formatPdfDate(raw)
              : String(raw);
        }
      }
    };
    const syncAvailability = (): void => {
      const ready = viewer.state.status === "ready" && viewer.state.active;
      action.disabled = !ready;
      action.setAttribute("aria-disabled", String(!ready));
      if (!ready) {
        clearRows();
        setStatus(null);
        dismiss();
      }
    };

    action.addEventListener(
      "click",
      () => {
        if (action.disabled || viewer.state.status !== "ready") return;
        const active = dialog.ownerDocument.activeElement as HTMLElement | null;
        const menu = menuPanel ?? root.querySelector<HTMLElement>(PDFJS_VIEWER_UI_HOOKS.menu.panel);
        const toggle =
          menuToggle ?? root.querySelector<HTMLButtonElement>(PDFJS_VIEWER_UI_HOOKS.menu.toggle);
        opener = active && menu?.contains(active) ? toggle : active;
        if (toggle?.getAttribute("aria-expanded") === "true") toggle.click();
        clearRows();
        setStatus("pdf.document-information-loading");
        dialog.setAttribute("aria-busy", "true");
        dialog.showModal();
        closeButton.focus();
        const currentRequest = ++request;
        void informationQuery()
          .then(result => {
            if (signal.aborted || currentRequest !== request || !dialog.open) return;
            dialog.removeAttribute("aria-busy");
            if (!result.ok) {
              setStatus("pdf.document-information-unavailable");
              if (result.reason === "error")
                Logger.error("PDF document information failed", result.error);
              return;
            }
            setStatus(null);
            render(result.information);
          })
          .catch(error => {
            if (signal.aborted || currentRequest !== request || !dialog.open) return;
            dialog.removeAttribute("aria-busy");
            setStatus("pdf.document-information-unavailable");
            Logger.error("PDF document information failed", error);
          });
      },
      { signal },
    );
    closeButton.addEventListener("click", dismiss, { signal });
    acceptButton.addEventListener("click", dismiss, { signal });
    dialog.addEventListener(
      "click",
      event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (
          event.clientX >= bounds.left &&
          event.clientX <= bounds.right &&
          event.clientY >= bounds.top &&
          event.clientY <= bounds.bottom
        )
          return;
        dismiss();
      },
      { signal },
    );
    dialog.addEventListener(
      "close",
      () => {
        request++;
        dialog.removeAttribute("aria-busy");
        const returnTarget = opener;
        opener = null;
        window.setTimeout(() => {
          if (!signal.aborted && returnTarget?.isConnected) returnTarget.focus();
        }, 0);
      },
      { signal },
    );
    root.addEventListener("pdf:statechange", syncAvailability, { signal });
    signal.addEventListener("abort", dismiss, { once: true });
    syncAvailability();
  }

  /** Releases every host listener that survives in caller-owned PDF markup. */
  private releaseViewerBindings(view: string): void {
    releasePdfBindingLifetime(this.bindingLifetimes, view);
  }

  /** Reflects package-owned rendering capabilities in the application's custom controls. */
  private syncRenderingProfileControls(
    group: HTMLElement | null,
    availableProfiles: readonly PdfjsViewerRenderingProfile[],
  ): void {
    if (!group) return;

    const available = new Set(availableProfiles);
    for (const input of group.querySelectorAll<HTMLInputElement>("[data-pdf-rendering-profile]")) {
      const profile = input.dataset.pdfRenderingProfile as PdfjsViewerRenderingProfile | undefined;
      const isAvailable = profile !== undefined && available.has(profile);
      input.disabled = !isAvailable;
      input.setAttribute("aria-disabled", String(!isAvailable));
      input.closest("label")?.toggleAttribute("hidden", !isAvailable);
      group.ownerDocument
        .querySelector<HTMLLabelElement>(`label[for="${CSS.escape(input.id)}"]`)
        ?.toggleAttribute("hidden", !isAvailable);
    }
    group.closest("fieldset")?.toggleAttribute("hidden", availableProfiles.length < 2);
  }

  /** Collects bindings from the current pane DOM, whether it was rendered by SSR or client navigation. */
  private collectUiBindings(root: HTMLElement): PdfjsViewerUiBindings | null {
    const required = <T extends Element>(scope: ParentNode, selector: string): T | null =>
      scope.querySelector<T>(selector);
    const controls = required<HTMLElement>(root, PDFJS_VIEWER_UI_HOOKS.root.controlDiscoveryScope);
    const container = required<HTMLElement>(root, PDFJS_VIEWER_UI_HOOKS.root.container);
    const printDialog = required<HTMLDialogElement>(root, PDFJS_VIEWER_UI_HOOKS.printSetup.dialog);
    if (!controls || !container || !printDialog) return null;

    const searchPanel = required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.search.panel);
    const menuPanel = required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.panel);
    const sidebar = required<HTMLElement>(root, PDFJS_VIEWER_UI_HOOKS.sidebar.container);
    const printForm = required<HTMLFormElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.form);
    if (!searchPanel || !menuPanel || !sidebar || !printForm) return null;

    const bindings: PdfjsViewerUiBindings = {
      controlDiscoveryScope: controls,
      container,
      navigation: {
        previous:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigation.previous) ??
          undefined,
        next:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigation.next) ?? undefined,
        pageNumber:
          required<HTMLInputElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigation.pageNumber) ??
          undefined,
        pageCount:
          required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigation.pageCount) ?? undefined,
      },
      navigationHistory: {
        back:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigationHistory.back) ??
          undefined,
        forward:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.navigationHistory.forward) ??
          undefined,
      },
      fullscreen: {
        toggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.fullscreen.toggle) ??
          undefined,
      },
      presentation: {
        toggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.presentation.toggle) ??
          undefined,
        controls:
          required<HTMLElement>(root, PDFJS_VIEWER_UI_HOOKS.presentation.controls) ?? undefined,
        previous:
          required<HTMLButtonElement>(root, PDFJS_VIEWER_UI_HOOKS.presentation.previous) ??
          undefined,
        next:
          required<HTMLButtonElement>(root, PDFJS_VIEWER_UI_HOOKS.presentation.next) ?? undefined,
        exit:
          required<HTMLButtonElement>(root, PDFJS_VIEWER_UI_HOOKS.presentation.exit) ?? undefined,
      },
      zoom: {
        fit: required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.zoom.fit) ?? undefined,
        fitMenu:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.zoom.fitMenu) ?? undefined,
        slider:
          required<HTMLInputElement>(controls, PDFJS_VIEWER_UI_HOOKS.zoom.slider) ?? undefined,
        sliderWrap:
          required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.zoom.sliderWrap) ?? undefined,
      },
      search: {
        toggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.search.toggle) ?? undefined,
        panel: searchPanel,
        input:
          required<HTMLInputElement>(searchPanel, PDFJS_VIEWER_UI_HOOKS.search.input) ?? undefined,
        previous:
          required<HTMLButtonElement>(searchPanel, PDFJS_VIEWER_UI_HOOKS.search.previous) ??
          undefined,
        next:
          required<HTMLButtonElement>(searchPanel, PDFJS_VIEWER_UI_HOOKS.search.next) ?? undefined,
        count: required<HTMLElement>(searchPanel, PDFJS_VIEWER_UI_HOOKS.search.count) ?? undefined,
        close:
          required<HTMLButtonElement>(searchPanel, PDFJS_VIEWER_UI_HOOKS.search.close) ?? undefined,
      },
      menu: {
        toggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.toggle) ?? undefined,
        panel: menuPanel,
        close:
          required<HTMLButtonElement>(menuPanel, PDFJS_VIEWER_UI_HOOKS.menu.close) ?? undefined,
        textSelectionToggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.textSelectionToggle) ??
          undefined,
        pageLayout:
          required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.pageLayout) ?? undefined,
        fitMode: required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.fitMode) ?? undefined,
        rotateCounterclockwise:
          required<HTMLButtonElement>(
            controls,
            PDFJS_VIEWER_UI_HOOKS.menu.rotateCounterclockwise,
          ) ?? undefined,
        resetRotation:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.resetRotation) ??
          undefined,
        rotateClockwise:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.rotateClockwise) ??
          undefined,
        renderingProfile:
          required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.renderingProfile) ?? undefined,
        screenWakeLock:
          required<HTMLElement>(controls, PDFJS_VIEWER_UI_HOOKS.menu.screenWakeLock) ?? undefined,
      },
      sidebar: {
        container: sidebar,
        toggle:
          required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.sidebar.toggle) ?? undefined,
        close:
          required<HTMLButtonElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.sidebar.close) ?? undefined,
        primaryViews:
          required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.sidebar.primaryViews) ?? undefined,
        moreToggle:
          required<HTMLButtonElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.sidebar.moreToggle) ??
          undefined,
        moreMenu:
          required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.sidebar.moreMenu) ?? undefined,
      },
      outline: {
        content: required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.outline.content) ?? undefined,
        filter: required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.outline.filter) ?? undefined,
        filterInput:
          required<HTMLInputElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.outline.filterInput) ??
          undefined,
      },
      thumbnails: {
        content:
          required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.thumbnails.content) ?? undefined,
      },
      attachments: {
        content:
          required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.attachments.content) ?? undefined,
      },
      layers: {
        content: required<HTMLElement>(sidebar, PDFJS_VIEWER_UI_HOOKS.layers.content) ?? undefined,
      },
      download:
        required<HTMLAnchorElement>(root, PDFJS_VIEWER_UI_HOOKS.actions.download) ?? undefined,
      downloadFilledDocument:
        required<HTMLButtonElement>(root, PDFJS_VIEWER_UI_HOOKS.actions.downloadFilledDocument) ??
        undefined,
      print:
        required<HTMLButtonElement>(controls, PDFJS_VIEWER_UI_HOOKS.actions.print) ?? undefined,
      printSetup: {
        dialog: printDialog,
        form: printForm,
        pages:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.pages) ??
          undefined,
        range:
          required<HTMLInputElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.range) ??
          undefined,
        rangeWrap:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.rangeWrap) ??
          undefined,
        layout:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.layout) ??
          undefined,
        side:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.side) ?? undefined,
        sideSelect:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.sideSelect) ??
          undefined,
        more:
          required<HTMLDetailsElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.more) ??
          undefined,
        sheet:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.sheet) ??
          undefined,
        customSheet:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.customSheet) ??
          undefined,
        customSheetWidth:
          required<HTMLInputElement>(
            printDialog,
            PDFJS_VIEWER_UI_HOOKS.printSetup.customSheetWidth,
          ) ?? undefined,
        customSheetHeight:
          required<HTMLInputElement>(
            printDialog,
            PDFJS_VIEWER_UI_HOOKS.printSetup.customSheetHeight,
          ) ?? undefined,
        customSheetUnit:
          required<HTMLSelectElement>(
            printDialog,
            PDFJS_VIEWER_UI_HOOKS.printSetup.customSheetUnit,
          ) ?? undefined,
        pageScaling:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.pageScaling) ??
          undefined,
        orientation:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.orientation) ??
          undefined,
        quality:
          required<HTMLSelectElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.quality) ??
          undefined,
        status:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.status) ?? undefined,
        progress:
          required<HTMLProgressElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.progress) ??
          undefined,
        guidance:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.guidance) ??
          undefined,
        fallbackWarning:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.fallbackWarning) ??
          undefined,
        close:
          required<HTMLButtonElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.close) ??
          undefined,
        cancel:
          required<HTMLButtonElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.cancel) ??
          undefined,
        source:
          required<HTMLButtonElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.source) ??
          undefined,
        submit:
          required<HTMLButtonElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.submit) ??
          undefined,
        submitDetail:
          required<HTMLElement>(printDialog, PDFJS_VIEWER_UI_HOOKS.printSetup.submitDetail) ??
          undefined,
      },
      documentProgress:
        required<HTMLProgressElement>(root, PDFJS_VIEWER_UI_HOOKS.documentProgress.indicator) ??
        undefined,
    };

    return bindings.navigation !== undefined &&
      Object.values(bindings.navigation).every(Boolean) &&
      bindings.zoom !== undefined &&
      Object.values(bindings.zoom).every(Boolean) &&
      bindings.fullscreen !== undefined &&
      Object.values(bindings.fullscreen).every(Boolean) &&
      bindings.presentation !== undefined &&
      Object.values(bindings.presentation).every(Boolean) &&
      bindings.search !== undefined &&
      Object.values(bindings.search).every(Boolean) &&
      bindings.menu !== undefined &&
      Object.values(bindings.menu).every(Boolean) &&
      bindings.sidebar !== undefined &&
      Object.values(bindings.sidebar).every(Boolean) &&
      bindings.outline !== undefined &&
      Object.values(bindings.outline).every(Boolean) &&
      bindings.thumbnails !== undefined &&
      Object.values(bindings.thumbnails).every(Boolean) &&
      bindings.attachments !== undefined &&
      Object.values(bindings.attachments).every(Boolean) &&
      bindings.layers !== undefined &&
      Object.values(bindings.layers).every(Boolean) &&
      Boolean(
        bindings.download &&
        bindings.downloadFilledDocument &&
        bindings.print &&
        bindings.documentProgress,
      ) &&
      bindings.printSetup !== undefined &&
      Object.values(bindings.printSetup).every(Boolean)
      ? bindings
      : null;
  }

  /** Shows a translated inline message when a PDF cannot be loaded. */
  private showPdfUnavailableMessage(pdfRootEl: HTMLElement): void {
    const container = pdfRootEl.querySelector<HTMLElement>(PDFJS_VIEWER_UI_HOOKS.root.container);
    if (!container) return;

    const key = "pdf.unavailable-offline";
    const message =
      this.translator.translateKey(key).trim() ||
      "This PDF is not available offline yet. Please open this book once while online.";

    container.innerHTML = "";
    const el = document.createElement("p");
    el.className = "pdf-status-message sb-muted";
    el.setAttribute("data-i18n", key);
    el.textContent = message;
    container.appendChild(el);
  }

  /** Wires the advanced search link inside the PDF search panel. */
  private wireAdvancedSearchLink(
    view: string,
    pdfRootEl: HTMLElement,
    signal: AbortSignal,
    searchInput?: HTMLInputElement,
  ): void {
    const input =
      searchInput ?? pdfRootEl.querySelector<HTMLInputElement>(PDFJS_VIEWER_UI_HOOKS.search.input);
    const link = pdfRootEl.querySelector<HTMLAnchorElement>(".pdf-search-advanced-link");
    if (!link) return;

    const update = () => this.updateAdvancedSearchLink(view);
    link.addEventListener("click", update, { once: true, signal });
    input?.addEventListener("input", update, { signal });
    input?.addEventListener("change", update, { signal });

    this.updateAdvancedSearchLink(view);
  }

  /** Updates the public search URL with the current book, variant, and query. */
  private updateAdvancedSearchLink(view: string): void {
    const pane = document.querySelector<HTMLElement>(`[data-sb-pane="${this.cssEscape(view)}"]`);
    if (!pane) return;
    const root = pane.querySelector<HTMLElement>(".pdf-viewer");
    if (!root) return;
    const searchInput = root.querySelector<HTMLInputElement>(".pdf-search-input");
    const link = root.querySelector<HTMLAnchorElement>(".pdf-search-advanced-link");
    if (!link) return;

    const meta = this.viewMeta.get(view);
    const query = String(searchInput?.value ?? "").trim();
    const sourceId = meta?.sourceId?.trim();
    const variant = meta?.variant?.trim();
    link.href = serializeViewUrl(
      {
        view: "search",
        lang: this.state.lang,
        search: {
          query,
          books: sourceId ? [sourceId] : "all",
          variants: variant ? [variant] : "all",
          entities: "all",
        },
      },
      { defaultLanguage: this.state.site.defaultLanguage ?? "en" },
    );
  }

  private replaceHashParam(paramName: string, value: string | null): void {
    const url = new URL(window.location.href);
    const raw = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const params = new URLSearchParams(raw);

    if (value == null || value === "") params.delete(paramName);
    else params.set(paramName, value);

    const next = params.toString();
    const nextHash = next ? `#${next}` : "";
    if (window.location.hash === nextHash) return;

    url.hash = next;
    window.history.replaceState(window.history.state, "", url);
  }

  /**
   * Escapes a string for safe use in CSS attribute selectors.
   */
  private cssEscape(s: string): string {
    const css = (globalThis as any).CSS;
    if (css && typeof css.escape === "function") return css.escape(s);
    return s.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }
}
